import React, { memo, useRef, useState, useCallback } from "react";

// interfaces
import { OnTransitionError, TRANSITION_ERROR_TYPES } from "@payment-ui/components/tick-screen/interfaces";

// components
import { LottieAnimation, useEffectOnce } from "@swiggy-private/core-ui";

// helpers
import { fetchTransitionMROStripLottie } from "@payment-ui/components/tick-screen/helpers";
import { TRANSITION_MRO_STRIP_LOTTIE_URL } from "@payment-ui/components/tick-screen/constants";

// styles
import { PlayerContainer } from "./styles";
import { isEmpty } from "@swiggy-private/js-utils";
import { TICK_SCREEN_STRIP_TEST_IDS } from "@swiggy-private/payment-test-ids";

interface TransitionFooterStripProps {
    onStripLottieComplete?: () => void;
    onError?: OnTransitionError;
}

const TransitionFooterStrip = ({ onStripLottieComplete, onError }: TransitionFooterStripProps) => {
    const isDisposedRef = useRef(false);
    const [lottieData, setLottieData] = useState<unknown>();

    const handleError = useCallback<OnTransitionError>(
        (errorType, error, url) => {
            if (isDisposedRef.current) {
                return;
            }
            onError?.(errorType, error, url);
        },
        [onError],
    );

    const onLottieError = useCallback(() => {
        handleError(TRANSITION_ERROR_TYPES.LOTTIE_ERROR);
    }, [handleError]);

    const loadStripLottie = useCallback(async () => {
        const data = await fetchTransitionMROStripLottie({
            url: TRANSITION_MRO_STRIP_LOTTIE_URL,
            onError: handleError,
        });
        if (!isDisposedRef.current) {
            setLottieData(data);
        }
    }, [handleError]);

    useEffectOnce(() => {
        isDisposedRef.current = false;
        try {
            void loadStripLottie();
        } catch (error) {
            // do nothing
        }

        return () => {
            isDisposedRef.current = true;
        };
    });

    return (
        <PlayerContainer data-testid={TICK_SCREEN_STRIP_TEST_IDS.CONTAINER} aria-hidden="true">
            {/* mounted only once the data is ready so the player never initialises empty */}
            {!isEmpty(lottieData) && (
                <LottieAnimation
                    visible
                    animationData={lottieData}
                    loop={false}
                    autoplay
                    playLottie
                    playIfVisible={false}
                    backwardSpeed={1}
                    forwardSpeed={1}
                    onPlayComplete={onStripLottieComplete}
                    onError={onLottieError}
                />
            )}
        </PlayerContainer>
    );
};

export default memo(TransitionFooterStrip);
